import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../../components/Seo";
import EditorialArticle from "./EditorialArticle";
import { getArticle, peekArticleCache } from "../../services/news.service";

function formatDate(val) {
  if (!val) return "";
  try { return new Date(val).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return val; }
}

function toIsoDate(val) {
  if (!val) return undefined;
  try {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  } catch {
    return undefined;
  }
}

// ── Article renderer ──────────────────────────────────────────────────────────
function ArticleBody({ content }) {
  if (!content) return null;
  const normalizedContent = String(content)
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00A0/g, " ");

  return (
    <div
      className="article-body"
      dangerouslySetInnerHTML={{ __html: normalizedContent }}
      style={{
        color:"var(--text-secondary)",
        fontSize:17,
      }}
    />
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function NewsArticlePage() {
  const { slug } = useParams();
  const initialCached = peekArticleCache(slug) || null;
  const [article, setArticle] = useState(initialCached);
  const [loading, setLoading] = useState(!initialCached);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    const cached = peekArticleCache(slug) || null;
    if (cached) {
      setArticle(cached);
      setNotFound(false);
      setLoading(false);
    } else {
      setLoading(true);
      setNotFound(false);
      setArticle(null);
    }

    getArticle(slug)
      .then(data => {
        if (!active) return;
        setArticle(data.article);
        setNotFound(false);
      })
      .catch(() => {
        if (!active) return;
        // Keep a previously fetched article on a transient failure.
        if (!cached) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return (
      <div style={{background:"var(--bg-primary)",paddingTop:80,minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <span style={{color:"var(--text-dim)",fontSize:14}}>Loading…</span>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div style={{background:"var(--bg-primary)",paddingTop:80,minHeight:"60vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
        <Seo title="Article Not Found" description="The requested research article could not be found." path={`/research/${slug}`} robots="noindex,nofollow" />
        <h1 style={{fontSize:32,fontWeight:700,color:"var(--text-primary)"}}>Article not found</h1>
        <Link to="/research" className="btn btn-primary">← Back to Research</Link>
      </div>
    );
  }

  if (article.editorial) return <EditorialArticle article={article} />;

  const articlePath = `/research/${article.slug || slug}`;
  const publishedIso = toIsoDate(article.createdAt || article.date);
  const ogImage = article.heroImage || "https://ateno.co/og.jpg";
  const metaDesc = article.metaDescription || article.subtitle || article.excerpt;
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.metaTitle || article.title,
    description: metaDesc,
    author: {
      "@type": "Organization",
      name: article.author || "Ateno Team",
    },
    publisher: {
      "@type": "Organization",
      name: "Ateno",
      logo: {
        "@type": "ImageObject",
        url: "https://ateno.co/favicon.png",
      },
    },
    mainEntityOfPage: `https://ateno.co${articlePath}`,
    image: [ogImage],
    datePublished: publishedIso,
    dateModified: toIsoDate(article.updatedAt) || publishedIso,
  };

  return (
    <div style={{background:"var(--bg-primary)",paddingTop:80}}>
      <Seo
        title={article.metaTitle || article.title}
        description={metaDesc}
        path={articlePath}
        type="article"
        image={ogImage}
        jsonLd={articleJsonLd}
      />
      {/* Hero */}
      <section className="section section-dark" style={{paddingBottom:48}}>
        <div className="container" style={{maxWidth:720}}>
          <Link to="/research" style={{fontSize:13,color:"var(--accent)",textDecoration:"none",display:"inline-flex",alignItems:"center",gap:4,marginBottom:28}}>
            ← Research
          </Link>

          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
            <span style={{
              fontSize:10,fontWeight:600,letterSpacing:".07em",textTransform:"uppercase",
              color:article.tagColor,background:`${article.tagColor}18`,
              border:`1px solid ${article.tagColor}33`,borderRadius:999,padding:"3px 10px",
            }}>{article.tag}</span>
            <span style={{fontSize:13,color:"var(--text-dim)"}}>
              {article.date || formatDate(article.createdAt)}
            </span>
            {article.readTime && (
              <>
                <span style={{color:"var(--text-faint)"}}>·</span>
                <span style={{fontSize:13,color:"var(--text-dim)"}}>{article.readTime}</span>
              </>
            )}
          </div>

          <h1 style={{fontSize:"clamp(24px,4vw,40px)",fontWeight:800,color:"var(--text-primary)",letterSpacing:"-.03em",lineHeight:1.2,marginBottom:18}}>
            {article.title}
          </h1>

          <p style={{fontSize:18,color:"var(--text-tertiary)",lineHeight:1.6,marginBottom:0}}>
            {article.excerpt}
          </p>

          {article.author && (
            <p style={{fontSize:13,color:"var(--text-dim)",marginTop:20}}>By {article.author}</p>
          )}

          {article.heroImage && (
            <img
              src={article.heroImage}
              alt=""
              style={{width:"100%",maxHeight:400,objectFit:"cover",borderRadius:12,marginTop:28}}
            />
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="container" style={{maxWidth:720,paddingTop:0,paddingBottom:0}}>
        <div className="divider" />
      </div>

      {/* Body */}
      <section className="section section-dark" style={{paddingTop:48}}>
        <div className="container" style={{maxWidth:720}}>
          {article.content ? (
            <ArticleBody content={article.content} />
          ) : (
            <div className="card-dark" style={{padding:"32px 28px",textAlign:"center"}}>
              <p style={{color:"var(--text-dim)",fontSize:14}}>
                Full article available at launch. Stay tuned!
              </p>
            </div>
          )}

          {/* Footer nav */}
          <div style={{marginTop:64,paddingTop:32,borderTop:"1px solid var(--border-subtle)"}}>
            <Link to="/research" style={{fontSize:14,color:"var(--accent)",textDecoration:"none",fontWeight:500}}>
              ← All articles
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
